import React, { useState, useEffect } from 'react';
import './index.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons';
import profilePicture from './profile.png';
import { Send, XCircle, Phone, MessageCircle, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Links = () => {
  const [showModal, setShowModal] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const links = [
    {
      title: 'Buy A TerpScoop',
      emoji: '🛍️',
      url: 'https://shop.terpmetrix.com',
    },
    {
      title: 'Terpscoops Instagram',
      emoji: '🥄',
      url: 'https://instagram.com/terpscoops',
    },
    {
      title: 'Terpmetrix Home',
      emoji: '🏠',
      url: 'https://terpmetrix.com',
    },
    {
      title: 'TerpFocus',
      emoji: '📸',
      url: 'https://terpfocus.com',
    },
    {
      title: 'TerpTier (Alpha)',
      emoji: '🔍',
      url: 'https://terptier.com',
    },
  ];

  const socialLinks = [
    { icon: faInstagram, url: 'https://instagram.com/terpmetrix', label: 'Instagram' },
    { icon: faTwitter, url: 'https://twitter.com/terpmetrix', label: 'Twitter' },
    { icon: faLinkedin, url: 'https://www.linkedin.com/company/terpmetrix', label: 'LinkedIn' },
  ];

  // ── Animation Variants ──
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.15,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 24, opacity: 0, filter: 'blur(4px)' },
    visible: {
      y: 0,
      opacity: 1,
      filter: 'blur(0px)',
      transition: { type: 'spring', stiffness: 260, damping: 22 },
    },
  };

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 20, filter: 'blur(4px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    transition: { duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] },
  });

  return (
    <>
      {/* ── Animated Background ── */}
      <div className="mesh-bg">
        <div className="mesh-orb" />
      </div>
      <div className="noise-overlay" />

      {/* ── Contact Modal ── */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="modal-card"
              initial={{ scale: 0.92, y: 30, opacity: 0, filter: 'blur(8px)' }}
              animate={{ scale: 1, y: 0, opacity: 1, filter: 'blur(0px)' }}
              exit={{ scale: 0.92, y: 30, opacity: 0, filter: 'blur(8px)' }}
              transition={{ type: 'spring', damping: 28, stiffness: 320 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.button
                onClick={() => setShowModal(false)}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                  transition: 'color 0.2s',
                }}
                whileHover={{ color: 'var(--accent)', scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <XCircle size={22} />
              </motion.button>

              <p
                style={{
                  fontSize: '1.35rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                  marginTop: 8,
                }}
              >
                Sam Steingard
              </p>
              <p
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--text-muted)',
                  marginBottom: 28,
                }}
              >
                Get in touch
              </p>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 20,
                }}
              >
                {[
                  { href: 'tel:3015254028', icon: <Phone size={26} />, label: 'Call' },
                  { href: 'sms:3015254028', icon: <MessageCircle size={26} />, label: 'Text' },
                  { href: 'mailto:sam@terpmetrix.com', icon: <Mail size={26} />, label: 'Email' },
                ].map((item, i) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    className="contact-option"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 + i * 0.08, type: 'spring', stiffness: 300 }}
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.92 }}
                    title={item.label}
                  >
                    {item.icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Main Content ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 16px 32px',
          justifyContent: 'center',
        }}
      >
        <motion.div
          className="glass-card"
          style={{
            width: '100%',
            maxWidth: 440,
            padding: '40px 32px 36px',
          }}
          initial={{ opacity: 0, y: 40, filter: 'blur(8px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* ── Profile ── */}
          <motion.div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 24,
            }}
            {...fadeUp(0.2)}
          >
            <div className="profile-ring">
              <motion.img
                src={profilePicture}
                alt="Terpmetrix"
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </motion.div>

          {/* ── Title ── */}
          <motion.h1
            style={{
              textAlign: 'center',
              fontSize: '1.75rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              marginBottom: 4,
              letterSpacing: '-0.02em',
            }}
            {...fadeUp(0.35)}
          >
            Terpmetrix
          </motion.h1>
          <motion.p
            style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: 'var(--text-secondary)',
              marginBottom: 24,
              fontWeight: 400,
            }}
            {...fadeUp(0.42)}
          >
            Terpene science, perfected.
          </motion.p>

          {/* ── Social Icons ── */}
          <motion.div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              marginBottom: 28,
            }}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {socialLinks.map((link, index) => (
              <motion.a
                key={index}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                className="social-icon"
                variants={itemVariants}
                whileHover={{ scale: 1.12, y: -3 }}
                whileTap={{ scale: 0.92 }}
                aria-label={link.label}
              >
                <FontAwesomeIcon icon={link.icon} />
              </motion.a>
            ))}
          </motion.div>

          {/* ── Get In Touch CTA ── */}
          <motion.div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 32,
            }}
            {...fadeUp(0.55)}
          >
            <motion.div
              className="cta-btn-wrapper"
              style={{ width: '66%' }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <button
                onClick={() => setShowModal(true)}
                className="cta-btn"
                id="get-in-touch-btn"
              >
                Get In Touch
              </button>
            </motion.div>
          </motion.div>

          {/* ── Link Buttons ── */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={mounted ? 'visible' : 'hidden'}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            {links.map((link, index) => (
              <motion.div key={index} variants={itemVariants}>
                <motion.a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="glass-link-btn"
                  id={`link-${link.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span style={{ position: 'relative', zIndex: 1 }}>
                    {link.title}{' '}
                    <span style={{ marginLeft: 6 }}>{link.emoji}</span>
                  </span>
                </motion.a>
              </motion.div>
            ))}
          </motion.div>

          {/* ── Newsletter ── */}
          <motion.div
            style={{
              marginTop: 40,
              textAlign: 'center',
            }}
            {...fadeUp(1.0)}
          >
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                marginBottom: 16,
                fontStyle: 'italic',
              }}
            >
              Sign up for the newsletter!
            </h2>

            <form
              name="mailing-list"
              method="POST"
              style={{ width: '100%' }}
            >
              <input type="hidden" name="form-name" value="mailing-list" />
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                }}
              >
                <label style={{ flex: 1 }}>
                  <input
                    name="email"
                    type="email"
                    placeholder="Email address"
                    className="glass-input"
                    id="newsletter-email-input"
                  />
                </label>
                <motion.button
                  type="submit"
                  className="send-btn"
                  id="newsletter-submit-btn"
                  whileHover={{ scale: 1.08, rotate: 5 }}
                  whileTap={{ scale: 0.92 }}
                >
                  <Send size={18} />
                </motion.button>
              </div>
            </form>
          </motion.div>
        </motion.div>

        {/* ── Footer ── */}
        <motion.p
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-muted)',
            marginTop: 28,
            letterSpacing: '0.02em',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.6 }}
        >
          © {new Date().getFullYear()} Terpmetrix
        </motion.p>
      </div>
    </>
  );
};

export default Links;